import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ContainerLineTimeline } from "./ContainerLineTimeline";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import type { ContainerOrderLine } from "../../types/containerOrders";

interface ContainerLineTimelinePopoverProps {
  line: ContainerOrderLine;
  /** The status word already translated — the header owns that vocabulary. */
  statusLabel: string;
  /** The status pill's colours, from CONTAINER_LINE_STATUS_STYLES. */
  statusClassName: string;
}

/** Panel geometry. Width is a ceiling; a narrow phone gets the screen less margins. */
const MAX_WIDTH = 340;
const GAP = 8;
const MARGIN = 8;
/** Below this the panel is not worth opening downwards — flip it instead. */
const MIN_USEFUL_HEIGHT = 220;
/**
 * The least the panel may be given, whichever way it opened.
 *
 * Deliberately far below the flip threshold: that one decides *which* side to
 * open on, and reusing it as the height floor would hand the panel more room
 * than the side it chose actually has — pushing it off the top of a short
 * viewport, which a fixed-position element cannot be scrolled back into.
 */
const MIN_HEIGHT = 120;
/**
 * How long the panel survives the pointer leaving it.
 *
 * The trigger and the panel are not DOM neighbours — the panel is portalled to
 * `body` — so crossing the few pixels between them fires a real `pointerleave`.
 * Without a grace period the panel would close under a pointer on its way in.
 */
const CLOSE_DELAY = 140;

/**
 * The container's journey, behind its status pill.
 *
 * The timeline has a rail of its own from `xl` up. Below that there is no room
 * for a second column, and the fallback was to lay the whole thing across the
 * top of the page — which pushed the container's actual details a screen down
 * and made every visit start by scrolling past a record most of them were not
 * there to read. It is reference material, and reference material should be
 * available rather than unavoidable.
 *
 * So below `xl` it lives here: hovered on a desktop, tapped on a phone, and
 * costing no vertical space either way. The pill is the right handle for it
 * because the timeline is what the pill is a summary of.
 *
 * Not rendered at all from `xl` up — see `useMediaQuery`. A CSS-hidden trigger
 * would leave the portalled panel behind, floating over a page whose rail had
 * just taken the job back.
 */
export function ContainerLineTimelinePopover({
  line,
  statusLabel,
  statusClassName,
}: ContainerLineTimelinePopoverProps) {
  const { t } = useTranslation("containerOrders");
  // One pixel below Tailwind's `xl`, which is where the rail appears.
  const railVisible = useMediaQuery("(min-width: 1280px)");

  const [open, setOpen] = useState(false);
  /**
   * Opened by a click rather than by a hover.
   *
   * A pinned panel ignores the pointer leaving: a reader who clicked wants to
   * move into the panel, scroll it, and read a driver's note without it
   * evaporating on the way. Hover alone stays cheap and closes itself.
   */
  const [pinned, setPinned] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: "down" | "up";
  } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const close = useCallback(() => {
    cancelClose();
    setOpen(false);
    setPinned(false);
  }, []);

  /** Leaving with the pointer, which a pinned panel ignores. */
  const scheduleClose = () => {
    if (pinned) return;
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  };

  useEffect(() => cancelClose, []);

  /**
   * Anchor the panel to the pill and clamp it into the viewport.
   *
   * Four-way: the width gives way on a narrow screen, the panel flips above the
   * pill when there is more room up there, and the height it is allowed is
   * whatever is actually left — the panel scrolls inside that rather than
   * running off the bottom of a phone.
   */
  const position = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const width = Math.min(MAX_WIDTH, vw - MARGIN * 2);

    // Right-aligned to the pill, then clamped — the pill sits in a header whose
    // right-hand side holds the page's buttons, so opening leftwards is what
    // keeps the panel over the page rather than off the edge of it.
    let left = rect.right - width;
    if (left + width > vw - MARGIN) left = vw - width - MARGIN;
    if (left < MARGIN) left = MARGIN;

    const spaceBelow = vh - rect.bottom - GAP - MARGIN;
    const spaceAbove = rect.top - GAP - MARGIN;
    const placement: "down" | "up" =
      spaceBelow >= MIN_USEFUL_HEIGHT || spaceBelow >= spaceAbove
        ? "down"
        : "up";

    setCoords({
      top: placement === "down" ? rect.bottom + GAP : rect.top - GAP,
      left,
      width,
      maxHeight: Math.max(
        MIN_HEIGHT,
        placement === "down" ? spaceBelow : spaceAbove,
      ),
      placement,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    position();

    const handler = () => position();
    // Capture, so a scroll inside either of the page's two panes is caught as
    // well as the window's own.
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open, position]);

  // Outside press and Escape, both aware that the panel is not inside the
  // trigger's subtree. `pointerdown` rather than `click` so the panel goes
  // before whatever was pressed reacts.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  // Widening past `xl` hands the timeline back to its rail; anything still open
  // here would be a second copy of it, floating.
  useEffect(() => {
    if (railVisible) close();
  }, [railVisible, close]);

  if (railVisible) {
    // The plain pill. From `xl` up the rail says all of this already, and a pill
    // that opens a duplicate of the column beside it is a trap, not an affordance.
    return (
      <span
        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClassName}`}
      >
        {statusLabel}
      </span>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        // Hover is the desktop affordance; the guard keeps a tap from opening
        // the panel on `pointerenter` and closing it again on the `click` that
        // follows — the classic double-fire that makes a hover menu unusable by
        // touch.
        onPointerEnter={(event) => {
          if (event.pointerType !== "mouse") return;
          cancelClose();
          setOpen(true);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType !== "mouse") return;
          scheduleClose();
        }}
        // Click covers touch and the keyboard both: Enter and Space on a button
        // arrive here. It pins, so the panel can then be read at leisure.
        onClick={() => {
          cancelClose();
          if (open && pinned) {
            close();
            return;
          }
          setOpen(true);
          setPinned(true);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t("linePage.timeline.title")}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-copper ${statusClassName}`}
      >
        {statusLabel}
        <Info className="h-3 w-3 opacity-70" />
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={t("linePage.timeline.title")}
            onPointerEnter={cancelClose}
            onPointerLeave={(event) => {
              if (event.pointerType !== "mouse") return;
              scheduleClose();
            }}
            style={{
              top: coords.top,
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxHeight,
              // Flipping upwards anchors the panel's bottom edge to the pill's
              // top, which is the one thing a `top` alone cannot express.
              transform:
                coords.placement === "up" ? "translateY(-100%)" : undefined,
            }}
            className="fixed z-50 overflow-y-auto overscroll-contain rounded-xl border border-[rgba(11,15,14,0.12)] bg-white p-4 shadow-xl"
          >
            <ContainerLineTimeline line={line} />
          </div>,
          document.body,
        )}
    </>
  );
}
